import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play, ArrowSquareOut, Eye, Clock } from "@phosphor-icons/react";
import axios from "axios";
import { SOCIALS, TWITCH_CHANNEL } from "../config/socials";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CLIPS_URL = `${SOCIALS.twitch.url}/clips?range=all`;

const GAME_BADGE = {
    "Just Chatting": "bg-pink-500/15 text-pink-200 border-pink-400/30",
    "Minecraft": "bg-emerald-500/15 text-emerald-200 border-emerald-400/30",
    "ROBLOX": "bg-rose-500/15 text-rose-200 border-rose-400/30",
    "Roblox": "bg-rose-500/15 text-rose-200 border-rose-400/30",
    "Grand Theft Auto V": "bg-amber-500/15 text-amber-200 border-amber-400/30",
    "Among Us": "bg-violet-500/15 text-violet-200 border-violet-400/30",
    "Five Nights at Freddy's": "bg-red-500/15 text-red-200 border-red-400/30",
};

const gameClass = (g) => GAME_BADGE[g] || "bg-white/5 text-white/70 border-white/15";

const timeAgo = (iso) => {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    const diffSec = Math.max(1, Math.floor((Date.now() - then) / 1000));
    const units = [
        ["y", 365 * 24 * 3600],
        ["mo", 30 * 24 * 3600],
        ["w", 7 * 24 * 3600],
        ["d", 24 * 3600],
        ["h", 3600],
        ["m", 60],
    ];
    for (const [label, sec] of units) {
        if (diffSec >= sec) return `${Math.floor(diffSec / sec)}${label} ago`;
    }
    return `${diffSec}s ago`;
};

const fmtDuration = (s) => {
    if (!s && s !== 0) return "";
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60);
    return m > 0 ? `${m}:${String(r).padStart(2, "0")}` : `0:${String(r).padStart(2, "0")}`;
};

export const Clips = () => {
    const [clips, setClips] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const { data } = await axios.get(`${API}/twitch/clips?limit=20`, { timeout: 9000 });
                if (mounted) setClips(data?.clips || []);
            } catch {
                if (mounted) setClips([]);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => {
            mounted = false;
        };
    }, []);

    return (
        <section className="relative py-10 lg:py-14" data-testid="clips-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="mb-10 flex items-end justify-between flex-wrap gap-4"
                >
                    <div>
                        <span className="eyebrow">Highlights</span>
                        <h2 className="font-display text-3xl sm:text-4xl text-white mt-2">
                            The <span className="gradient-text">replay</span> reel
                        </h2>
                        <p className="text-white/55 mt-2 max-w-xl text-sm">
                            Pulled straight from <span className="font-mono text-white">twitch.tv/{TWITCH_CHANNEL}/clips</span>, sorted by views.
                        </p>
                    </div>
                    <a
                        href={CLIPS_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-sweep inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white text-sm hover:border-white/30 transition"
                        data-testid="view-all-clips-btn"
                    >
                        View all on Twitch <ArrowSquareOut size={13} weight="bold" />
                    </a>
                </motion.div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="clips-loading">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="rounded-2xl border border-white/10 bg-obsidian-900/60 overflow-hidden">
                                <div className="aspect-video bg-white/5 animate-pulse" />
                                <div className="p-4 space-y-2">
                                    <div className="h-3 w-3/4 bg-white/10 rounded animate-pulse" />
                                    <div className="h-2 w-1/3 bg-white/10 rounded animate-pulse" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : clips.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="rounded-2xl border border-white/10 bg-obsidian-900/60 p-12 text-center"
                        data-testid="clips-empty-state"
                    >
                        <div className="h-14 w-14 mx-auto rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-5">
                            <Play size={20} className="text-brand-cyan ml-0.5" weight="fill" />
                        </div>
                        <h3 className="font-display text-xl text-white mb-2">Clips drop in soon</h3>
                        <p className="text-white/55 text-sm max-w-md mx-auto">
                            Couldn't reach Twitch right now. Try again in a bit, or open the clips page directly.
                        </p>
                        <div className="mt-6 flex justify-center gap-3 flex-wrap">
                            <a
                                href={SOCIALS.twitch.url}
                                target="_blank"
                                rel="noreferrer"
                                className="btn-sweep inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-black text-sm font-semibold hover:bg-brand-cyan transition"
                                data-testid="clips-watch-live-btn"
                            >
                                Watch live <ArrowSquareOut size={13} weight="bold" />
                            </a>
                            <a
                                href={CLIPS_URL}
                                target="_blank"
                                rel="noreferrer"
                                className="btn-sweep inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-white/80 text-sm hover:border-white/30 transition"
                                data-testid="clips-twitch-link-btn"
                            >
                                Open clips page
                            </a>
                        </div>
                    </motion.div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="clips-grid">
                        {clips.map((c, i) => (
                            <motion.a
                                key={c.id || c.slug || i}
                                href={c.url || `https://clips.twitch.tv/${c.slug}`}
                                target="_blank"
                                rel="noreferrer"
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: Math.min(i, 8) * 0.04 }}
                                className="group block rounded-2xl overflow-hidden border border-white/10 bg-obsidian-900/60 card-tilt"
                                data-testid={`clip-card-${i}`}
                            >
                                <div className="relative aspect-video bg-black overflow-hidden">
                                    {c.thumbnail ? (
                                        <img
                                            src={c.thumbnail}
                                            alt={c.title}
                                            loading="lazy"
                                            className="w-full h-full object-cover transition duration-500 group-hover:scale-105"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-white/5" />
                                    )}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
                                    <div className="absolute top-2.5 left-2.5 flex items-center gap-2">
                                        {c.game && (
                                            <span className={`px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-mono ${gameClass(c.game)}`}>
                                                {c.game}
                                            </span>
                                        )}
                                    </div>
                                    {typeof c.duration === "number" && (
                                        <span className="absolute bottom-2.5 right-2.5 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] font-mono">
                                            {fmtDuration(c.duration)}
                                        </span>
                                    )}
                                    <div className="absolute bottom-2.5 left-2.5 flex items-center gap-2 text-[10px] text-white/85 font-mono">
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/70">
                                            <Eye size={10} weight="bold" /> {(c.view_count ?? 0).toLocaleString()}
                                        </span>
                                        {c.created_at && (
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/70">
                                                <Clock size={10} weight="bold" /> {timeAgo(c.created_at)}
                                            </span>
                                        )}
                                    </div>
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                                        <span className="h-12 w-12 rounded-full bg-white/90 text-black flex items-center justify-center">
                                            <Play size={18} weight="fill" className="ml-0.5" />
                                        </span>
                                    </div>
                                </div>
                                <div className="p-4">
                                    <h3 className="font-medium text-white text-sm leading-snug group-hover:text-brand-cyan transition line-clamp-2 min-h-[2.5rem]">
                                        {c.title}
                                    </h3>
                                    {c.creator && (
                                        <div className="text-[11px] text-white/45 font-mono mt-2">
                                            clipped by {c.creator}
                                        </div>
                                    )}
                                </div>
                            </motion.a>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};
